import { useCallback, useState } from 'react';
import { getProductAttributes, getProductAttributeTerms, getRawProducts } from '../../../services/woocommerce';
import { WooCommerceDataProvider } from '../../adapters/catalog/woocommerce';

export function useSets() {
  const [sets, setSets] = useState<Array<{ id: string; name: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Try dedicated sets endpoint first
      try {
        const directSets = await WooCommerceDataProvider.getSets();
        if (Array.isArray(directSets) && directSets.length > 0) {
          setSets(directSets);
          return;
        }
      } catch (_) {
        // ignore and fallback
      }

      // Try attribute-based set retrieval next
      let list: Array<{ id: string; name: string }> = [];
      try {
        const attributes = await getProductAttributes({ per_page: 100 });
        const setAttr = attributes.find((a: any) => {
          const slug = String(a?.slug || '').toLowerCase();
          const name = String(a?.name || '').toLowerCase();
          return slug === 'set' || slug === 'pa_set' || name === 'set';
        });
        if (setAttr) {
          const attrId = setAttr?.id ?? setAttr?.attribute_id ?? setAttr?.term_id;
          const terms = await getProductAttributeTerms(attrId, { per_page: 100 });
          list = (Array.isArray(terms) ? terms : []).map((t: any) => ({ id: String(t.slug || t.id || t.name).toLowerCase(), name: t.name || t.slug }));
        }
      } catch (_) {
        // ignore and fallback
      }

      // Fallback: derive from products if no attribute found or empty
      if (list.length === 0) {
        try {
          const rawProducts = await getRawProducts({ per_page: 100 });
          const setMap = new Map<string, string>();
          (Array.isArray(rawProducts.data) ? rawProducts.data : []).forEach((p: any) => {
            const attrs = p.attributes || [];
            attrs.forEach((attr: any) => {
              const attrName = String(attr.name || '').toLowerCase();
              if (attrName === 'set' || attrName === 'pa_set') {
                const options = attr.options || [];
                options.forEach((opt: string) => {
                  if (opt && !setMap.has(opt.toLowerCase())) {
                    setMap.set(opt.toLowerCase(), opt);
                  }
                });
              }
            });
          });
          list = Array.from(setMap.entries()).map(([id, name]) => ({ id, name }));
        } catch (_) {
          // ignore
        }
      }

      setSets(list);
    } catch (e: any) {
      console.error('📦 [useSets] Error fetching sets:', e);
      setError(e?.message || 'Failed to fetch sets');
    } finally {
      setLoading(false);
    }
  }, []);

  return { sets, loading, error, refetch };
}

